import { useCallback, useEffect, useRef } from "react";
import { sendTeamSpaceTyping } from "../api/client";

const START_DEBOUNCE_MS = 350;
const IDLE_STOP_MS = 3000;
const HEARTBEAT_MS = 2500;

/**
 * Envia indicador de digitação para outros membros do espaço (debounce + heartbeat).
 */
export function useTeamTypingNotifier(spaceId, { company = null, enabled = true } = {}) {
  const idleTimerRef = useRef(null);
  const startTimerRef = useRef(null);
  const heartbeatRef = useRef(null);
  const isTypingRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (startTimerRef.current) clearTimeout(startTimerRef.current);
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    idleTimerRef.current = null;
    startTimerRef.current = null;
    heartbeatRef.current = null;
  }, []);

  const stopTyping = useCallback(() => {
    clearTimers();
    if (!enabled || !spaceId || !isTypingRef.current) return;
    isTypingRef.current = false;
    sendTeamSpaceTyping(spaceId, false, company).catch(() => {});
  }, [spaceId, company, enabled, clearTimers]);

  const startHeartbeat = useCallback(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    heartbeatRef.current = setInterval(() => {
      if (isTypingRef.current && spaceId) {
        sendTeamSpaceTyping(spaceId, true, company).catch(() => {});
      }
    }, HEARTBEAT_MS);
  }, [spaceId, company]);

  const notifyActivity = useCallback(() => {
    if (!enabled || !spaceId) return;

    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(stopTyping, IDLE_STOP_MS);

    if (isTypingRef.current) return;

    if (startTimerRef.current) clearTimeout(startTimerRef.current);
    startTimerRef.current = setTimeout(() => {
      isTypingRef.current = true;
      sendTeamSpaceTyping(spaceId, true, company).catch(() => {});
      startHeartbeat();
    }, START_DEBOUNCE_MS);
  }, [spaceId, company, enabled, stopTyping, startHeartbeat]);

  useEffect(() => () => stopTyping(), [stopTyping]);

  useEffect(() => {
    isTypingRef.current = false;
    clearTimers();
  }, [spaceId, clearTimers]);

  return { notifyActivity, stopTyping };
}
